import {ACCESS_TOKEN, COMPANY_ID, USER_ID} from '@constants/auth.constant'
import {LocalStorageService} from '@services/utils/localStorage.service'
import axios from 'axios'
import {ENV} from './ENV.config'

/**
 * This console talks to TWO back ends, so the instance is built by a factory and both get the
 * same auth headers and the same 401/error handling:
 *   - axiosIns     -> labor-service (REACT_APP_GETWAY): every /worker and /wrk-* endpoint.
 *   - hrmAxiosIns  -> hrm-service (REACT_APP_HRM_GETWAY): the masters workers SHARE with employees
 *                     (departments, sections, units, shifts, locations, employment statuses, master
 *                     meta, tax config). HRM owns and edits those; this console only reads them —
 *                     see @services/api/hrm-lookup.service.ts.
 * One token works on both: core's AuthenticationFilter accepts any live sso token, whatever app
 * it was issued for.
 */
const makeInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
    },
  })

  instance.interceptors.request.use(
    (config: any) => {
      const accessToken = LocalStorageService.get(ACCESS_TOKEN) || null
      const uid = LocalStorageService.get(USER_ID) || null
      const cid = LocalStorageService.get(COMPANY_ID) || null

      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
      if (uid) config.headers.uid = uid
      if (cid) config.headers.cid = cid

      return config
    },
    (error) => {
      if (error.response) {
        return Promise.reject({
          ...error.response.data,
          ...{code: error.response.data.code || error.status},
        })
      }

      return Promise.reject({
        body: false,
        code: 404,
        msg: 'Server not responding',
      })
    }
  )

  instance.interceptors.response.use(
    (res: any) => {
      // A binary endpoint (the wage documents) returns raw bytes, deliberately NOT wrapped in an
      // AppResponse envelope -- an envelope around a PDF is a corrupt PDF. A Blob has no `code`, so it
      // would fall through to the reject below and fail EVERY download. Hand it back untouched and let
      // the caller check the content type (utility/downloadBlob). Same as store's.
      if (res?.data instanceof Blob) return res

      if (res.data.code === 200) return {...res.data}

      if (res.data.code === 401) {
        LocalStorageService.clear()
        window.location.reload()
      }
      return Promise.reject({
        body: res.data.body,
        code: res.data.code,
        msg: res.data.msg,
      })
    },
    (error) => {
      if (error?.response) {
        if (error?.response?.data?.code === 401) {
          LocalStorageService.clear()
          window.location.reload()
        }

        // A refusal from a service's ACL or role gate is a real HTTP 403 whose sentence is in
        // `message` (core PermissionInterceptor, each service's AuthzInterceptor). Every caller toasts
        // `msg`, and axios 0.26 has no `error.status`, so without this a refusal reached the screen
        // with no code and an empty toast.
        if (error.response.status === 403) {
          const data = error.response.data || {}
          return Promise.reject({
            code: 403,
            msg: data.message || data.msg || 'You do not have permission to do this.',
            body: {},
          })
        }

        if (error?.response?.data?.error) {
          return Promise.reject({
            code: error?.response?.data?.code,
            msg: error?.response?.data?.error,
            body: {},
          })
        }

        return Promise.reject({
          ...error?.response?.data,
          ...{code: error.response.data.code || error.status},
        })
      } else {
        return Promise.reject({
          code: 500,
          msg: 'Server not responding',
          body: {},
        })
      }
    }
  )

  return instance
}

export const axiosIns = makeInstance(ENV.apiEndpoint)
export const hrmAxiosIns = makeInstance(ENV.hrmApiEndpoint)
